import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { watchGuards, createInvite, updateUser } from "../../services/firestore";
import {
  watchInvoicesForGuard, setGuardPaid, clearGuardPaid, recordGuardCheck,
  payCycleFor, payCycleLabel,
} from "../../services/invoices";
import PhoneInput from "../../components/PhoneInput";
import "./OwnerLayout.css";
import { invoiceLabel } from "../../services/invoiceLabel";

function formatMoney(n) {
  const v = Number(n) || 0;
  return `$${v.toFixed(2)}`;
}
// Job date "2026-10-21" as 10/21/26, read as a local date (Date.parse would
// treat it as UTC midnight and show the day before in US time zones).
function formatJobDate(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  return y && m && d ? `${m}/${d}/${String(y).slice(2)}` : "";
}
function formatDate(ts) {
  if (!ts) return "";
  let ms = null;
  if (typeof ts === "number") ms = ts;
  else if (typeof ts === "string") ms = Date.parse(ts);
  else if (ts.toMillis) ms = ts.toMillis();
  else if (ts.seconds != null) ms = ts.seconds * 1000;
  if (ms == null || Number.isNaN(ms)) return "";
  const d = new Date(ms);
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}

export default function Guards() {
  const { userProfile } = useAuth();
  const [guards, setGuards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    const unsub = watchGuards(userProfile.companyId, (data) => {
      setGuards(data);
      setLoading(false);
    });
    return unsub;
  }, [userProfile]);

  const filtered = guards.filter((g) =>
    !search || g.name?.toLowerCase().includes(search.toLowerCase()) || g.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (selected) {
    return <GuardDetail guard={selected} onBack={() => setSelected(null)} companyId={userProfile?.companyId} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Guards</div>
          <div className="page-subtitle">{guards.length} guard{guards.length !== 1 ? "s" : ""}</div>
        </div>
        <button className="btn btn-blue" onClick={() => setShowInvite(true)}>+ Invite guard</button>
      </div>

      <div className="page-body">
        <input
          style={{ width: "100%", maxWidth: 320, marginBottom: 14 }}
          placeholder="Search guards..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (
          <div className="loading-state">Loading guards...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div>{search ? "No guards match your search" : "No guards yet"}</div>
            {!search && <button className="btn btn-blue" onClick={() => setShowInvite(true)}>Invite your first guard</button>}
          </div>
        ) : (
          <div className="panel">
            <div style={{
              display: "grid", gridTemplateColumns: "36px 1fr 160px 90px 90px 80px 80px",
              padding: "7px 14px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8",
              fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
            }}>
              <span></span><span>Name · Email</span><span>Phone</span>
              <span>Photo</span><span>TWIC</span><span>Tax info</span><span>Status</span>
            </div>
            {filtered.map((guard) => (
              <div key={guard.id} onClick={() => setSelected(guard)} style={{
                display: "grid", gridTemplateColumns: "36px 1fr 160px 90px 90px 80px 80px",
                padding: "10px 14px", borderBottom: "1px solid #f0f0f0",
                alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer",
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#fafbfc"}
                onMouseLeave={(e) => e.currentTarget.style.background = ""}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", background: "#185FA5",
                  color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}>
                  {guard.name?.charAt(0) || "?"}
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>{guard.name || "—"}</div>
                  <div style={{ fontSize: 10, color: "#888" }}>{guard.email}</div>
                </div>
                <span style={{ color: "#666" }}>{guard.phone || "—"}</span>
                <StatusDot value={guard.photoUrl} />
                <StatusDot value={guard.twicCardUrl} expiry={guard.twicExpiry} />
                <StatusDot value={guard.tin} label="Tax" />
                <span className={`chip ${guard.active !== false ? "chip-green" : "chip-gray"}`}>
                  {guard.invitePending ? "Pending" : guard.active !== false ? "Active" : "Inactive"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showInvite && (
        <InviteModal
          companyId={userProfile?.companyId}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}

function StatusDot({ value, expiry, label }) {
  const hasValue = !!value;
  const isExpired = expiry && new Date(expiry) < new Date();
  const color = !hasValue ? "#e8e8e8" : isExpired ? "#a32d2d" : "#2d7a2d";
  const text = !hasValue ? "Missing" : isExpired ? "Expired" : "On file";
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <span style={{ color: "#666" }}>{text}</span>
    </span>
  );
}

function InviteModal({ companyId, onClose }) {
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [phone, setPhone]     = useState("");
  const [saving, setSaving]   = useState(false);
  const [inviteId, setInviteId] = useState(null);
  const [copied, setCopied]   = useState(false);
  const [error, setError]     = useState("");

  const inviteLink = inviteId
    ? `${window.location.origin}/invite/${inviteId}`
    : "";

  async function handleSend() {
    if (!name.trim() || !email.trim()) return setError("Name and email are required.");
    setSaving(true);
    setError("");
    try {
      const ref = await createInvite({ name, email, phone, companyId, role: "guard" });
      setInviteId(ref.id);
    } catch {
      setError("Failed to create invite. Please try again.");
    }
    setSaving(false);
  }

  function handleCopy() {
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const smsBody = encodeURIComponent(
    `Hi ${name}, you've been invited to join VeloceFlow as a guard. Tap this link to set up your account:\n${inviteLink}`
  );
  const smsHref = `sms:${phone.replace(/\D/g, "")}?body=${smsBody}`;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 12, width: 400, boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Invite guard</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          {inviteId ? (
            /* ── Success: show link ── */
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ background: "#d4edda", border: "1px solid #b3d9be", borderRadius: 8, padding: "10px 13px", fontSize: 12, color: "#1a4a1a" }}>
                ✓ Invite created for <strong>{name}</strong>. Link expires in 48 hours.
              </div>

              {/* Link box */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Invite link</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    readOnly
                    value={inviteLink}
                    onClick={(e) => e.target.select()}
                    style={{ flex: 1, fontSize: 11, background: "#f8f9fb", color: "#333", cursor: "text" }}
                  />
                  <button
                    className="btn btn-blue"
                    onClick={handleCopy}
                    style={{ whiteSpace: "nowrap", minWidth: 88 }}
                  >
                    {copied ? "Copied ✓" : "Copy link"}
                  </button>
                </div>
              </div>

              {/* SMS shortcut */}
              {phone && (
                <a
                  href={smsHref}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 7, padding: "9px 14px", borderRadius: 8,
                    background: "#f0faf4", border: "1px solid #b3d9be",
                    color: "#1a4a1a", fontSize: 12, fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  💬 Open SMS to {name}
                </a>
              )}

              <button className="btn" onClick={onClose} style={{ width: "100%", marginTop: 2 }}>Close</button>
            </div>
          ) : (
            /* ── Form ── */
            <>
              {error && (
                <div style={{ background: "#fff0f0", border: "1px solid #f5a5a5", borderRadius: 7, padding: "8px 10px", fontSize: 11, color: "#a32d2d", marginBottom: 12 }}>
                  {error}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Full name *</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Guard's full name" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Email address *</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="guard@email.com" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Phone <span style={{ fontWeight: 400, color: "#aaa" }}>(optional — for SMS shortcut)</span></label>
                  <PhoneInput value={phone} onChange={setPhone} placeholder="(704) 555-0100" />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button className="btn" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-blue" onClick={handleSend} disabled={saving} style={{ flex: 1 }}>
                  {saving ? "Creating..." : "Create invite link"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GuardDetail({ guard, onBack, companyId }) {
  const [form, setForm] = useState({
    name: guard.name || "",
    phone: guard.phone || "",
    twicExpiry: guard.twicExpiry || "",
    taxName: guard.taxName || "",
    tin: guard.tin || "",
    address: guard.address || "",
    defaultPayRate: guard.defaultPayRate || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    setSaving(true);
    await updateUser(guard.id, form);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn btn-sm" onClick={onBack}>← Back</button>
          <div>
            <div className="page-title">{guard.name || "Guard"}</div>
            <div className="page-subtitle">{guard.email}</div>
          </div>
        </div>
        <button className="btn btn-blue" onClick={handleSave} disabled={saving}>
          {saved ? "Saved ✓" : saving ? "Saving..." : "Save changes"}
        </button>
      </div>

      <div className="page-body">
        {/* Pay summary — top of the page since this is what the owner
            most often comes here for. Shows what's outstanding, what's
            been paid this cycle, and a per-invoice mark-paid action. */}
        <GuardPayPanel guardId={guard.id} guardName={guard.name} companyId={companyId} />

        <div className="two-col">
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Profile</div></div>
            {[
              { label: "Full name", key: "name" },
              { label: "Phone", key: "phone", type: "tel" },
              { label: "Address", key: "address" },
              { label: "Default pay rate ($/hr)", key: "defaultPayRate", type: "number" },
            ].map(({ label, key, type = "text" }) => (
              <div key={key} className="field-row">
                <div className="field-label">{label}</div>
                <input type={type} value={form[key]} onChange={(e) => set(key, e.target.value)} style={{ flex: 1 }} />
              </div>
            ))}
          </div>
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Compliance & tax</div></div>
            <div className="field-row">
              <div className="field-label">TWIC card</div>
              <div className="field-value">{guard.twicCardUrl ? <a href={guard.twicCardUrl} target="_blank" rel="noreferrer" style={{ color: "#185FA5" }}>View upload</a> : <span style={{ color: "#bbb" }}>Not uploaded</span>}</div>
            </div>
            <div className="field-row">
              <div className="field-label">TWIC expiry</div>
              <input type="date" value={form.twicExpiry} onChange={(e) => set("twicExpiry", e.target.value)} style={{ flex: 1 }} />
            </div>
            <div className="field-row">
              <div className="field-label">Tax name</div>
              <input value={form.taxName} onChange={(e) => set("taxName", e.target.value)} style={{ flex: 1 }} />
            </div>
            <div className="field-row">
              <div className="field-label">TIN / SSN (last 4)</div>
              <input value={form.tin} onChange={(e) => set("tin", e.target.value)} style={{ flex: 1 }} />
            </div>
            <div className="field-row">
              <div className="field-label">Profile photo</div>
              <div className="field-value">{guard.photoUrl ? <img src={guard.photoUrl} alt="guard" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} /> : <span style={{ color: "#bbb" }}>Not uploaded</span>}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Per-guard pay summary ──────────────────────────────────
// Owner-facing view of every invoice tied to this guard:
//   - Cycle picker (current + recent cycles, derived from invoices)
//   - Totals: earned this cycle, paid out, outstanding
//   - Per-invoice rows with a 💵 Pay / Edit toggle
// Same data and actions as the Invoices page's row-level controls,
// just scoped to one guard so the owner can reconcile a pay cycle
// without filtering the global invoice list.
function GuardPayPanel({ guardId, guardName, companyId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycleFilter, setCycleFilter] = useState("all");
  const [showCheck, setShowCheck] = useState(false);

  useEffect(() => {
    if (!companyId || !guardId) return;
    const unsub = watchInvoicesForGuard(companyId, guardId, (data) => {
      setInvoices(data);
      setLoading(false);
    });
    return unsub;
  }, [companyId, guardId]);

  // Derived cycle options — only cycles the guard actually has invoices in.
  const cycleOptions = Array.from(new Set(invoices.map((i) => i.payCycle).filter(Boolean)))
    .sort()
    .reverse();

  const filtered = cycleFilter === "all"
    ? invoices
    : invoices.filter((i) => i.payCycle === cycleFilter);

  // Oldest job first, by the job's date (most invoices have no usable
  // completedAt — see invoices.js).
  const sorted = filtered.slice().sort((a, b) =>
    String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || "")));

  // Totals. "Billed" is what the broker was billed for this guard's jobs —
  // not the guard's pay (no pay rates are saved), so there's no "owed"
  // amount; instead count the jobs with no payment entered yet.
  const billedTotal = sorted.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const paidOut = sorted.reduce((s, i) => s + (Number(i.guardPaidAmount) || 0), 0);
  const unpaidJobs = sorted.filter((i) => i.guardPaidAmount == null && i.payGuard !== false).length;

  async function handlePay(inv) {
    // Blank unless already paid — the broker bill total is NOT the guard's pay.
    const suggested = inv.guardPaidAmount != null ? inv.guardPaidAmount : "";
    const input = window.prompt(
      `How much did you pay ${guardName || "this guard"} for ${invoiceLabel(inv) || `invoice ${inv.invoiceNumber}`}?`,
      String(suggested)
    );
    if (input === null) return;
    const amount = Number(input);
    if (Number.isNaN(amount)) {
      alert("Please enter a number.");
      return;
    }
    await setGuardPaid(inv.id, amount);
  }

  async function handleClear(inv) {
    if (!window.confirm(`Clear pay record for ${invoiceLabel(inv) || `invoice ${inv.invoiceNumber}`}?`)) return;
    await clearGuardPaid(inv.id);
  }

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div className="panel-title">💵 Pay summary</div>
          <div className="panel-subtitle">
            Jobs this guard worked (by job date) and what you've paid them.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-sm btn-blue" onClick={() => setShowCheck(true)}>🧾 Record a check</button>
          <label style={{ fontSize: 11, color: "#666" }}>Cycle:</label>
          <select
            value={cycleFilter}
            onChange={(e) => setCycleFilter(e.target.value)}
            style={{ fontSize: 12, padding: "4px 6px", borderRadius: 6, border: "1px solid #ddd" }}
          >
            <option value="all">All cycles</option>
            {cycleOptions.map((c) => (
              <option key={c} value={c}>{payCycleLabel(c)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Totals strip */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
        padding: "12px 14px", borderBottom: "1px solid #f0f0f0", gap: 8,
      }}>
        <div>
          <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>Billed for their jobs</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{formatMoney(billedTotal)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>Paid out</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0E7C46" }}>{formatMoney(paidOut)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>Not paid yet</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: unpaidJobs > 0 ? "#a32d2d" : "#888" }}>
            {unpaidJobs} job{unpaidJobs === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 20, fontSize: 11, color: "#888", textAlign: "center" }}>Loading...</div>
      ) : sorted.length === 0 ? (
        <div style={{ padding: 20, fontSize: 12, color: "#aaa", textAlign: "center", fontStyle: "italic" }}>
          {cycleFilter === "all" ? "No invoices yet for this guard." : "No invoices in this cycle."}
        </div>
      ) : (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "100px 1fr 80px 90px 90px 110px",
            padding: "8px 14px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8",
            fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
          }}>
            <span>Job #</span><span>Broker</span><span>Cycle</span>
            <span>Job date</span><span>Billed</span><span>Paid</span>
          </div>
          {sorted.map((inv) => (
            <div key={inv.id} style={{
              display: "grid", gridTemplateColumns: "100px 1fr 80px 90px 90px 110px",
              padding: "10px 14px", borderBottom: "1px solid #f0f0f0",
              alignItems: "center", gap: 8, fontSize: 12,
            }}>
              <span style={{ fontWeight: 600, color: "#185FA5" }}>
                {invoiceLabel(inv) || `#${inv.invoiceNumber || "?"}`}
              </span>
              <span style={{ color: "#444" }}>{inv.brokerName || "—"}</span>
              <span style={{ fontSize: 10, color: "#666" }}>{(inv.payCycle || "").slice(5) || "—"}</span>
              <span style={{ color: "#666" }}>{formatJobDate(inv.scheduledDate) || formatDate(inv.completedAt)}</span>
              <span style={{ fontWeight: 600 }}>{formatMoney(inv.total)}</span>
              <span>
                {inv.guardPaidAmount != null ? (
                  <button
                    className="btn btn-sm"
                    onClick={() => handleClear(inv)}
                    title={`Paid ${formatMoney(inv.guardPaidAmount)}${inv.guardPaidAt ? " on " + formatDate(inv.guardPaidAt) : ""} — click to edit`}
                    style={{ background: "#E0F4E8", color: "#0E7C46", borderColor: "#a9d4c2", fontSize: 11 }}
                  >
                    💵 {formatMoney(inv.guardPaidAmount)}
                  </button>
                ) : (
                  <button
                    className="btn btn-sm btn-blue"
                    onClick={() => handlePay(inv)}
                    style={{ fontSize: 11 }}
                  >
                    💵 Pay
                  </button>
                )}
              </span>
            </div>
          ))}
        </>
      )}
      {showCheck && (
        <RecordCheckModal
          guardName={guardName}
          // Every unpaid job for this guard (ignores the cycle filter), oldest first.
          invoices={invoices
            .filter((i) => i.status !== "void" && i.guardPaidAmount == null && i.payGuard !== false)
            .sort((a, b) => String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || "")))}
          onClose={() => setShowCheck(false)}
        />
      )}
    </div>
  );
}

// ── Record a check ─────────────────────────────────────────
// One check from the owner's stub, spread over the jobs it covers. Each job
// gets its own amount (typed, or "Split evenly"); Save needs the amounts to
// add up to the check. Money out then counts each amount in its job's month.
function RecordCheckModal({ guardName, invoices, onClose }) {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const [checkAmount, setCheckAmount] = useState("");
  const [paidDate, setPaidDate] = useState(`${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`);
  const [checkNumber, setCheckNumber] = useState("");
  const [amounts, setAmounts] = useState({}); // invoiceId -> "123.45" (ticked jobs only)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const ticked = invoices.filter((i) => amounts[i.id] !== undefined);
  const applied = Math.round(ticked.reduce((s, i) => s + (Number(amounts[i.id]) || 0), 0) * 100) / 100;
  const total = Math.round((Number(checkAmount) || 0) * 100) / 100;
  const balanced = total > 0 && ticked.length > 0 && Math.abs(applied - total) < 0.005
    && ticked.every((i) => Number(amounts[i.id]) > 0);

  function toggle(id) {
    setAmounts((a) => {
      const next = { ...a };
      if (next[id] !== undefined) delete next[id]; else next[id] = "";
      return next;
    });
  }
  // Even split in cents; leftover pennies go on the first jobs.
  function splitEvenly() {
    if (!ticked.length || !(total > 0)) return;
    const cents = Math.round(total * 100);
    const base = Math.floor(cents / ticked.length);
    let extra = cents - base * ticked.length;
    const next = {};
    ticked.forEach((i) => { next[i.id] = ((base + (extra-- > 0 ? 1 : 0)) / 100).toFixed(2); });
    setAmounts(next);
  }
  async function save() {
    setSaving(true);
    setError("");
    try {
      await recordGuardCheck(ticked.map((i) => ({ invoiceId: i.id, amount: amounts[i.id] })), paidDate, checkNumber.trim());
      onClose();
    } catch (e) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  }
  const jobDay = (ymd) => {
    const [y, m, d] = String(ymd || "").split("-").map(Number);
    return y ? new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "—";
  };
  const field = { display: "block", fontSize: 10, fontWeight: 700, color: "#888", marginBottom: 3 };

  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 520, maxHeight: "92vh",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>Record a check · {guardName || "guard"}</div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, color: "#888", cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: "14px 18px", overflowY: "auto", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 14 }}>
            <label><span style={field}>CHECK AMOUNT</span>
              <input type="number" inputMode="decimal" min="0" step="0.01" placeholder="$0.00" value={checkAmount}
                onChange={(e) => setCheckAmount(e.target.value)} style={{ width: "100%", fontSize: 16, fontWeight: 700 }} /></label>
            <label><span style={field}>DATE PAID</span>
              <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} style={{ width: "100%", fontSize: 16 }} /></label>
            <label><span style={field}>CHECK # (optional)</span>
              <input type="text" value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} style={{ width: "100%", fontSize: 16 }} /></label>
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7684", letterSpacing: 0.4, marginBottom: 6 }}>
            JOBS THIS CHECK COVERS (UNPAID, OLDEST FIRST)
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button className="btn btn-sm" style={{ flex: 1 }} onClick={splitEvenly} disabled={!ticked.length || !(total > 0)}>Split evenly</button>
            <button className="btn btn-sm" style={{ flex: 1 }} onClick={() => setAmounts({})} disabled={!ticked.length}>Clear</button>
          </div>
          {invoices.length === 0 && (
            <div style={{ padding: 16, fontSize: 13, color: "#999", textAlign: "center" }}>No unpaid jobs for this guard.</div>
          )}
          {invoices.map((inv) => {
            const on = amounts[inv.id] !== undefined;
            return (
              <div key={inv.id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", marginBottom: 6,
                border: `1px solid ${on ? "#185FA5" : "#e3e6ea"}`, background: on ? "#f5f9ff" : "#fff", borderRadius: 10,
              }}>
                <input type="checkbox" checked={on} onChange={() => toggle(inv.id)} style={{ width: 20, height: 20, flexShrink: 0 }} />
                <div onClick={() => toggle(inv.id)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{jobDay(inv.scheduledDate)} · {invoiceLabel(inv) || `#${inv.invoiceNumber || "?"}`}</div>
                  <div style={{ fontSize: 11, color: "#777" }}>
                    {inv.brokerName || "—"}{inv.hoursCharged ? ` · ${inv.hoursCharged} hrs` : ""}
                  </div>
                </div>
                <input type="number" inputMode="decimal" min="0" step="0.01" placeholder="—" disabled={!on}
                  value={on ? amounts[inv.id] : ""}
                  onChange={(e) => setAmounts((a) => ({ ...a, [inv.id]: e.target.value }))}
                  style={{ width: 92, textAlign: "right", fontWeight: 700, fontSize: 15 }} />
              </div>
            );
          })}
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid #e8e8e8" }}>
          <div style={{
            display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, marginBottom: 10,
            color: balanced ? "#1f6b35" : "#a36b1d",
          }}>
            <span>{ticked.length} job{ticked.length === 1 ? "" : "s"} · {formatMoney(applied)} of {formatMoney(total)}</span>
            <span>{balanced ? "✓ balanced" : total > 0 ? `${formatMoney(Math.abs(total - applied))} ${applied > total ? "over" : "left"}` : "enter the check amount"}</span>
          </div>
          {error && <div style={{ fontSize: 12, color: "#a32d2d", marginBottom: 8 }}>{error}</div>}
          <button className="btn btn-blue" style={{ width: "100%", minHeight: 44 }} disabled={!balanced || saving} onClick={save}>
            {saving ? "Saving..." : "Save check"}
          </button>
        </div>
      </div>
    </div>
  );
}
